import { apiClient } from './ApiClient';

// Interfaces matching Plaid API
interface PlaidAccount {
  account_id: string;
  item_id: string;
  name: string;
  type: string;
  subtype: string | null;
  mask: string | null;
  balances: {
    available: number | null;
    current: number;
    limit: number | null;
    iso_currency_code: string | null;
  };
  institution_id: string;
  institution_name?: string;
}

interface PlaidAccountDetails {
  account_id: string;
  balance: {
    current: number;
    available: number | null;
  };
}

interface PlaidTransaction {
  transaction_id: string;
  account_id: string;
  date: string;
  name: string;
  amount: number;
  category: string[] | null;
  merchant_name: string | null;
  pending: boolean;
}

// Backend account structure (from accounts table)
interface BankAccount {
  id: string;
  user_id: string;
  institution_name: string;
  institution_id: string;
  account_name: string;
  access_token: string;
  item_id: string;
  last_fetched: string | null;
  created_at: string;
  updated_at: string;
}

// Request interfaces
interface LinkAccountRequest {
  publicToken: string;
}

interface SyncTransactionsRequest {
  accountId: string;
  from?: string;
  to?: string;
}

interface AccountBalanceResponse {
  account_id: string;
  balance: {
    current: number;
    available: number | null;
  };
  last_updated: string | null;
}

interface SyncTransactionsResponse {
  message: string;
  count: number;
  transactions: Array<{
    id: number;
    account_id: string;
    name: string;
    category: string;
    amount: number;
    payee: string;
    transaction_type: 'INCOME' | 'EXPENSE';
    date: string;
    bank_txn_id: string;
  }>;
}

interface Transaction {
  id: number;
  name: string;
  category: string;
  amount: number;
  payee: string;
  transaction_type: 'INCOME' | 'EXPENSE';
  date: string;
  bank_txn_id: string;
}

class BankAccountService {
  async getBankAccounts(): Promise<BankAccount[]> {
    const response = await apiClient.get('/bank-accounts');
    return response.data;
  }

  async createLinkToken(): Promise<{ link_token: string }> {
    const response = await apiClient.post('/bank-accounts/link-token');
    return response.data;
  }

  async linkBankAccount(data: LinkAccountRequest): Promise<BankAccount[]> {
    const response = await apiClient.post('/bank-accounts/link', data);
    return response.data;
  }

  async syncTransactions(
    data: SyncTransactionsRequest,
  ): Promise<SyncTransactionsResponse> {
    const response = await apiClient.post('/bank-accounts/sync', data);
    return response.data;
  }

  async getAccountBalance(accountId: string): Promise<AccountBalanceResponse> {
    const response = await apiClient.get(`/bank-accounts/${accountId}/balance`);
    return response.data;
  }

  async unlinkAccount(accountId: string): Promise<void> {
    await apiClient.delete(`/bank-accounts/${accountId}`);
  }

  async getAccountTransactions(accountId: string): Promise<Transaction[]> {
    const response = await apiClient.get(
      `/bank-accounts/${accountId}/transactions`,
    );
    return response.data;
  }
}

export default new BankAccountService();

// Export types for use in components
export type {
  PlaidAccount,
  PlaidAccountDetails,
  PlaidTransaction,
  BankAccount,
  LinkAccountRequest,
  SyncTransactionsRequest,
  AccountBalanceResponse,
  SyncTransactionsResponse,
  Transaction,
};
